import { FC } from "react";
import { UseFormReturn } from "react-hook-form";
import { validatePhoneNumberLength } from "libphonenumber-js";
import { makeStyles } from "tss-react/mui";
import {
  MiDetailFields,
  MiFileInput,
  MiProfilePicture,
} from "@miplatform/components";
import { INPUT_TYPES, TIMEZONE_OPTIONS } from "@miplatform/constants";
import { getPhoneNumber } from "@miplatform/utils";
import { mdiPlusCircle } from "@mdi/js";
import { Box } from "@mui/material";
import { ConfigAccordionHeader } from "../../../components/ConfigAccordionHeader/ConfigAccordionHeader";
import { IUserFormData } from "../../ConfigUsers.types";

interface UserDetailsTabProps {
  form: UseFormReturn<IUserFormData>;
  extraTabProps?: {
    profilePictureSrc?: string | null;
    setPendingProfilePicture: (file: File) => void;
    isReadOnly?: boolean;
    isCreatingNewUser?: boolean;
  };
}

export const UserDetailsTabView: FC<UserDetailsTabProps> = ({
  form,
  extraTabProps,
}: UserDetailsTabProps) => {
  const { control, setValue } = form;
  const { classes } = useStyles();
  const {
    profilePictureSrc,
    setPendingProfilePicture,
    isReadOnly,
    isCreatingNewUser,
  } = extraTabProps || {};

  const userBasicInformationFields = [
    {
      label: "First Name",
      name: "firstName",
      formControl: control,
      required: true,
      width: "25%",
      disabled: isReadOnly,
    },
    {
      label: "Last Name",
      name: "lastName",
      formControl: control,
      required: true,
      width: "25%",
      disabled: isReadOnly,
    },
    {
      label: "Display Name",
      name: "displayName",
      formControl: control,
      required: true,
      width: "25%",
      disabled: isReadOnly,
    },
    {
      label: "Username",
      name: isCreatingNewUser ? "email" : "userName",
      formControl: control,
      width: "25%",
      disabled: true,
    },
  ];

  const userContactInformationFields = [
    {
      label: "Phone Number",
      name: "primaryPhoneNumber",
      formControl: control,
      disabled: isReadOnly,
      setValue: (e: React.ChangeEvent<HTMLInputElement>) => {
        const phoneNumber = getPhoneNumber(e.target.value);
        if (validatePhoneNumberLength(phoneNumber, "US") === "TOO_LONG") {
          return;
        }
        setValue("primaryPhoneNumber", phoneNumber, {
          shouldValidate: true,
          shouldDirty: true,
        });
      },
    },
    {
      label: "Email",
      name: "email",
      formControl: control,
      required: true,
      disabled: isReadOnly,
    },
    {
      label: "Preferred Contact Method",
      name: "preferredContactMethod",
      formControl: control,
      fieldType: INPUT_TYPES.SELECT,
      options: [
        { label: "Email", value: "Email" },
        { label: "Phone", value: "Phone" },
      ],
      disabled: isReadOnly,
    },
  ];

  const userTimeInformationFields = [
    {
      label: "Location",
      name: "primaryLocation",
      formControl: control,
      disabled: isReadOnly,
    },
    {
      label: "Time Zone",
      name: "timezone",
      formControl: control,
      disabled: isReadOnly,
      fieldType: INPUT_TYPES.SELECT,
      options: TIMEZONE_OPTIONS,
    },
  ];

  const userPreferencesFields = [
    {
      label: "Language",
      name: "language",
      formControl: control,

      fieldType: INPUT_TYPES.SELECT,
      options: [
        { label: "English", value: "en" },
        { label: "Spanish", value: "es" },
      ],
      disabled: isReadOnly,
    },
  ];

  const handleEditProfilePicture = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      setPendingProfilePicture?.(file);
    }
  };

  const [firstName, lastName] = form.watch(["firstName", "lastName"]);

  return (
    <Box className={classes.userDetailsTabViewContainer}>
      <ConfigAccordionHeader title="Profile Picture">
        <Box className={classes.profilePictureContainer}>
          <MiProfilePicture
            name={`${firstName} ${lastName}`}
            size={80}
            src={profilePictureSrc || undefined}
          />
          <MiFileInput
            title="Choose Image"
            startIcon={{ path: mdiPlusCircle }}
            customClassName={classes.fileInput}
            acceptFiles="image/*"
            onChange={handleEditProfilePicture}
            multiple={false}
          />
        </Box>
      </ConfigAccordionHeader>
      <ConfigAccordionHeader title="Basic Information">
        <MiDetailFields detailFields={userBasicInformationFields} />
      </ConfigAccordionHeader>
      <ConfigAccordionHeader title="Additional Information">
        <MiDetailFields detailFields={userContactInformationFields} />
      </ConfigAccordionHeader>
      <ConfigAccordionHeader title="Time Information">
        <MiDetailFields detailFields={userTimeInformationFields} />
      </ConfigAccordionHeader>
      <ConfigAccordionHeader title="Preferences">
        <MiDetailFields detailFields={userPreferencesFields} />
      </ConfigAccordionHeader>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  userDetailsTabViewContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  profilePictureContainer: {
    gap: theme.spacing(4),
    alignItems: "center",
    display: "flex",
    flexDirection: "row",
  },
  fileInput: {
    borderStyle: "dashed",
    padding: theme.spacing(2, 3),
  },
}));
